import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, PencilIcon } from 'lucide-react';
import { useFormState, FormData } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { TCPAConsent } from '../components/TCPAConsent';
import { usePageMeta } from '../hooks/usePageMeta';
import { educationLevels, degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
interface LocationState {
  degreeInterest: string;
  areaOfStudy: string;
  specialization: string;
}
export function ExpandedFormPage() {
  usePageMeta('Complete Your Profile | OnlineCollegeGuide', 'Fill out your profile to get matched with accredited online degree programs that fit your goals, schedule, and budget.', {
    ogImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;
  // Redirect if no state
  useEffect(() => {
    if (!state) {
      navigate('/', {
        replace: true
      });
    }
    window.scrollTo(0, 0);
  }, [state, navigate]);
  const {
    formData,
    errors,
    updateFormData,
    validateExpandedForm
  } = useFormState(state ? {
    degreeInterest: state.degreeInterest,
    areaOfStudy: state.areaOfStudy,
    specialization: state.specialization
  } : undefined);
  if (!state) return null;
  // Resolve labels for the summary bar
  const degreeName = degreeInterestOptions.find(d => d.value === state.degreeInterest)?.type || '';
  const focusName = focusOptions.find(f => f.value === state.areaOfStudy)?.type || '';
  const specName = specializationOptions.find(s => s.value === state.specialization)?.type || '';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateExpandedForm()) {
      // TODO: Send formData to matching API here
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Form submitted:', cleanedFormData);
      navigate('/results', {
        state: {
          formData: cleanedFormData
        }
      });
    }
  };
  const selectClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg bg-white appearance-none cursor-pointer ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg shadow-sm ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const radioGroupClasses = (field: keyof FormData, value: string) => `flex-1 cursor-pointer border rounded-lg py-3 px-4 text-center text-sm font-medium transition-all duration-150 ${formData[field] === value ? 'border-navy bg-navy/5 text-navy ring-1 ring-navy' : 'border-gray-300 text-gray-600 hover:border-navy/40'}`;
  return <main className="flex-grow bg-light min-h-screen" data-id="element-1515" data-mp="3c5e9wgtfv7fn">
      {/* Top bar with selections summary */}
      <div className="hero-bg text-white" data-id="element-1516" data-mp="2yac5xz85js5h">
        <div className="max-w-2xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-1517" data-mp="33a81rb6p4d9y">
          <div className="flex items-center gap-2 text-sm" data-id="element-1518" data-mp="2ocfnemcvxuxc">
            <ShieldCheckIcon className="w-4 h-4 text-gold flex-shrink-0" data-id="element-1519" data-mp="2tdligqu0pua1" />
            <span className="text-gray-300" data-id="element-1520" data-mp="2tapmh81qdfo8">Your selections:</span>
            <span className="font-semibold" data-id="element-1521" data-mp="2fevldb777ulo">{degreeName}</span>
            <span className="text-gray-400" data-id="element-1522" data-mp="2kblzhszg0g4g">›</span>
            <span className="font-semibold" data-id="element-1523" data-mp="2kehy7jj478ty">{focusName}</span>
            <span className="text-gray-400" data-id="element-1524" data-mp="dmzbe60oist0">›</span>
            <span className="font-semibold text-gold" data-id="element-1525" data-mp="dk38s4dvzfy8">{specName}</span>
          </div>
          <Link to="/" className="flex items-center gap-1 text-xs text-gray-300 hover:text-white transition-colors" data-id="element-1526" data-mp="iiy2w2hb0qxn">
            <PencilIcon className="w-3 h-3" data-id="element-1527" data-mp="29p2nv63nk2g1" />
            Edit
          </Link>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="max-w-2xl mx-auto px-4 py-8 sm:py-12" data-id="element-1528" data-mp="24ppqvsj2hezq">
        {/* Progress */}
        <div className="mb-8" data-id="element-1529" data-mp="1vqvx09x4f2pv">
          <div className="flex justify-between items-center mb-2" data-id="element-1530" data-mp="1vr4eyg83oy7x">
            <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-1531" data-mp="1qx06fsj6si4q">
              Step 2 of 2
            </span>
            <span className="text-xs font-medium text-gray-500" data-id="element-1532" data-mp="1h0eyaybveqwo">
              Almost done!
            </span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-1533" data-mp="1czbho9m00que">
            <div className="h-full bg-gold rounded-full w-1/2 transition-all duration-500" data-id="element-1534" data-mp="17zqn0bdv34nh" />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-1535" data-mp="3jmam6210z2z5">
          <div className="bg-navy px-6 py-4" data-id="element-1536" data-mp="3oku5duo8i0nt">
            <h1 className="text-white font-bold text-lg tracking-wide text-center" data-id="element-1537" data-mp="3snqu834dat07">
              Complete Your Profile
            </h1>
            <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1538" data-mp="1gebhjnu67i6">
              Tell us a bit about yourself so we can find the best matches.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6" data-id="element-1539" data-mp="34xwgzkg4z2ff">
            {/* Education Level */}
            <div data-id="element-1540" data-mp="39wr2wv9eh73r">
              <label htmlFor="currentEducation" className="block text-sm font-semibold text-gray-800 mb-1.5" data-id="element-1541" data-mp="39w7q1sylx8wl">
                Highest Level of Education Completed
              </label>
              <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses('currentEducation')} data-id="element-1542" data-mp="2h9d8p4x36pnc">
                <option value="" data-id="element-1543" data-mp="3pi2gkbehp1tm">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-1544" data-mp="3kk0vxm1f0ug1">
                    {level}
                  </option>)}
              </select>
              {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1545" data-mp="27m8uhff1zt1">
                  {errors.currentEducation}
                </p>}
            </div>

            {/* US Citizen */}
            <div data-id="element-1546" data-mp="36kj22s99iqzt">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-1547" data-mp="36jzvg7p2bp13">
                Are you a U.S. Citizen?
              </label>
              <div className="flex gap-3" data-id="element-1548" data-mp="3fk66lmxagcx3">
                <div onClick={() => updateFormData('isUSCitizen', 'yes')} className={radioGroupClasses('isUSCitizen', 'yes')} role="radio" aria-checked={formData.isUSCitizen === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'yes')} data-id="element-1549" data-mp="3flzd927wix53">
                  Yes
                </div>
                <div onClick={() => updateFormData('isUSCitizen', 'no')} className={radioGroupClasses('isUSCitizen', 'no')} role="radio" aria-checked={formData.isUSCitizen === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'no')} data-id="element-1550" data-mp="puua8o9q4ops">
                  No
                </div>
              </div>
              {errors.isUSCitizen && <p className="mt-1 text-xs text-red-600" data-id="element-1551" data-mp="tc8d8ylfzju7">
                  {errors.isUSCitizen}
                </p>}
            </div>

            {/* Military */}
            <div data-id="element-1552" data-mp="1h4yo14hlzx2w">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-1553" data-mp="1m1fiegfej7of">
                U.S. Military Affiliation?
              </label>
              <div className="flex gap-3" data-id="element-1554" data-mp="17c9lx07y7vk8">
                <div onClick={() => updateFormData('isMilitary', 'yes')} className={radioGroupClasses('isMilitary', 'yes')} role="radio" aria-checked={formData.isMilitary === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'yes')} data-id="element-1555" data-mp="1cbmabpegxaox">
                  Yes
                </div>
                <div onClick={() => updateFormData('isMilitary', 'no')} className={radioGroupClasses('isMilitary', 'no')} role="radio" aria-checked={formData.isMilitary === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'no')} data-id="element-1556" data-mp="4qh0vedubpzh">
                  No
                </div>
              </div>
              {errors.isMilitary && <p className="mt-1 text-xs text-red-600" data-id="element-1557" data-mp="3n5ridxchar66">{errors.isMilitary}</p>}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 pt-6" data-id="element-1558" data-mp="ipp0g8dx1047">
              <h3 className="text-sm font-semibold text-gray-800 mb-4" data-id="element-1559" data-mp="iq86aqqajy1v">
                Contact Information
              </h3>
            </div>

            {/* Name Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1560" data-mp="9nyxq6el2er4">
              <div data-id="element-1561" data-mp="2ze2le3oppeiw">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1562" data-mp="2zem3pli9y4xg">
                  First Name
                </label>
                <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses('firstName')} placeholder="Jane" data-id="element-1563" data-mp="2uhc7nn0dqv0n" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1564" data-mp="1ixww9axkun4d">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-1565" data-mp="1npn7dj4c9ewu">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1566" data-mp="1nq6es5wd4kwm">
                  Last Name
                </label>
                <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses('lastName')} placeholder="Doe" data-id="element-1567" data-mp="1xm9t0a4ho9zx" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1568" data-mp="26nr57herzrjc">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div data-id="element-1569" data-mp="89o2qhtxk11c">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1570" data-mp="8a7oyaqm5bl6">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses('email')} placeholder="jane.doe@example.com" data-id="element-1571" data-mp="1fiikjtmmv5b1" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1572" data-mp="1ohdrx91eylfx">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div data-id="element-1573" data-mp="1tijlta6t8er1">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1574" data-mp="1tevqq6lpvka7">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses('phone')} placeholder="(555)123-4567" maxLength={13} data-id="element-1575" data-mp="22gv7qywqeod5" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1576" data-mp="2c6y85k66n4li">{errors.phone}</p>}
            </div>

            {/* Address + Zip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1577" data-mp="2m5x88ecvtrww">
              <div className="sm:col-span-2" data-id="element-1578" data-mp="2m65moaonl5a7">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1579" data-mp="aiagmbcqzj37">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal" data-id="element-1580" data-mp="5io300t0c0tv">(optional)</span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-1581" data-mp="kwezhvhgyuy" />
              </div>
              <div data-id="element-1582" data-mp="oagzw6b7666v">
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1583" data-mp="oc1u1utlnn47">
                  Zip Code
                </label>
                <input type="text" id="zipCode" value={formData.zipCode} onChange={e => updateFormData('zipCode', e.target.value)} className={inputClasses('zipCode')} placeholder="12345" maxLength={5} data-id="element-1584" data-mp="ekxeasw3ny8b" />
                {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1585" data-mp="1781f0lo5tqea">{errors.zipCode}</p>}
              </div>
            </div>

            {/* TCPA */}
            <TCPAConsent checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} data-id="element-1586" data-mp="31j1834pk4m97" />

            {/* Submit */}
            <button type="submit" className="w-full bg-cta hover:bg-cta-dark text-white font-bold py-4 px-6 rounded-lg text-base tracking-wide uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta-dark shadow-md" data-id="element-1587" data-mp="2rqlndzmvljkm">
              Get My Results
            </button>

            {/* Security note */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 pt-2" data-id="element-1588" data-mp="28sj29vxg86s3">
              <LockIcon className="w-3.5 h-3.5" data-id="element-1589" data-mp="2dqknonicki6q" />
              Your information is secure and will never be sold.
            </div>
          </form>
        </div>
      </motion.div>
    </main>;
}